import { type TeamMember } from "../types";

interface Props {
  team: TeamMember[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  onChange: (i: number, field: keyof TeamMember, value: string) => void;
}

const ROLES = [
  "Curé",
  "Vicaire",
  "Recteur",
  "Doyen",
  "Aumônier",
  "Prêtre",
  "Diacre",
  "Responsable pastoral",
];

export default function StepTeam({ team, onAdd, onRemove, onChange }: Props) {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-stone-400 leading-relaxed" style={{ fontFamily: "'Nunito', sans-serif" }}>
        Ajoutez les membres de l&apos;équipe presbytérale de la paroisse (prêtres, diacres, responsables).
      </p>

      {team.map((member, i) => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden">
          {/* Member header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-navy-100 overflow-hidden">
                {member.image ? (
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <i className="ri-user-line text-navy-900 text-sm" />
                )}
              </div>
              <p className="font-bold text-stone-700 text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {member.name || `Membre ${i + 1}`}
              </p>
            </div>
            {team.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-red-50 cursor-pointer hover:bg-red-100 transition-colors"
              >
                <i className="ri-delete-bin-line text-red-400 text-sm" />
              </button>
            )}
          </div>

          <div className="px-4 py-3 flex flex-col gap-3">
            {/* Nom */}
            <div>
              <label className="block text-xs font-semibold text-stone-400 mb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
                Nom complet
              </label>
              <input
                type="text"
                value={member.name}
                onChange={(e) => onChange(i, "name", e.target.value)}
                placeholder="ex: Père Jean-Baptiste Diallo"
                className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-700 outline-none"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              />
            </div>

            {/* Rôle */}
            <div>
              <label className="block text-xs font-semibold text-stone-400 mb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
                Rôle / Fonction
              </label>
              <div className="relative">
                <select
                  value={member.role}
                  onChange={(e) => onChange(i, "role", e.target.value)}
                  className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-700 outline-none appearance-none cursor-pointer"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-stone-400 text-sm" />
                </div>
              </div>
            </div>

            {/* Photo URL */}
            <div>
              <label className="block text-xs font-semibold text-stone-400 mb-1" style={{ fontFamily: "'Nunito', sans-serif" }}>
                URL photo <span className="text-stone-300 font-normal">(optionnel)</span>
              </label>
              <input
                type="url"
                value={member.image}
                onChange={(e) => onChange(i, "image", e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm text-stone-700 outline-none"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              />
            </div>
          </div>
        </div>
      ))}

      {/* Add member button */}
      <button
        type="button"
        onClick={onAdd}
        className="w-full py-3 border-2 border-dashed border-stone-200 rounded-2xl flex items-center justify-center gap-2 cursor-pointer hover:border-navy-300 hover:bg-navy-50/30 transition-all"
      >
        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-stone-100">
          <i className="ri-add-line text-stone-500 text-sm" />
        </div>
        <span className="text-sm font-semibold text-stone-400" style={{ fontFamily: "'Nunito', sans-serif" }}>
          Ajouter un membre
        </span>
      </button>
    </div>
  );
}
